export type GamePackSummaryValue = {
  count: number;
  tickets: number;
  amount: number;
};

class GamePackSummaryModel {
  private _data: Record<string, GamePackSummaryValue>;

  constructor(summaryData: any = {}) {
    this._data = {
      active_packs_on_counter: this._normalizeValue(summaryData?.active_packs_on_counter),
      active_packs_in_backstock: this._normalizeValue(summaryData?.active_packs_in_backstock),
      not_activated_packs: this._normalizeValue(summaryData?.not_activated_packs),
      weekly_sales: this._normalizeValue(summaryData?.weekly_sales),
    };
  }

  private _normalizeValue(value: any): GamePackSummaryValue {
    if (!value) {
      return { count: 0, tickets: 0, amount: 0 };
    }
    return {
      count: Number(value.count ?? 0),
      tickets: Number(value.tickets ?? 0),
      amount: Number(value.amount ?? 0),
    };
  }

  get activePacksOnCounter(): GamePackSummaryValue {
    return this._data.active_packs_on_counter;
  }

  get activePacksInBackstock(): GamePackSummaryValue {
    return this._data.active_packs_in_backstock;
  }

  get weeklySales(): GamePackSummaryValue {
    return this._data.weekly_sales;
  }

  get notActivatedPacks(): GamePackSummaryValue {
    return this._data.not_activated_packs;
  }

  static fromServerData(serverData: any = {}) {
    return new GamePackSummaryModel(serverData?.summary);
  }
}

class GamePackGameModel {
  private _data: any;

  constructor(gameData: any = {}) {
    this._data = gameData;
  }

  get gameId(): string {
    return this._data?.game_id ?? '';
  }

  get upc(): string {
    return this._data?.upc ?? '';
  }

  get name(): string {
    return this._data?.name ?? '';
  }

  get startDate(): string {
    return this._data?.start_date ?? '';
  }

  get endDate(): string {
    return this._data?.end_date ?? '';
  }

  get rate(): number {
    return Number(this._data?.rate ?? 0);
  }

  get ticketsPerPack(): number {
    return Number(this._data?.tickets_per_pack ?? 0);
  }

  get activatedCountPacks(): number {
    return Number(this._data?.activated_count?.packs ?? 0);
  }

  get activatedCountTickets(): number {
    return Number(this._data?.activated_count?.tickets ?? 0);
  }

  get activatedOnCounterPacks(): number {
    return Number(this._data?.activated_on_counter?.packs ?? 0);
  }

  get activatedOnCounterTickets(): number {
    return Number(this._data?.activated_on_counter?.tickets ?? 0);
  }

  get onCounterValue(): number {
    return Number(this._data?.on_counter_value ?? 0);
  }

  get activatedBackstockPacks(): number {
    return Number(this._data?.activated_backstock?.packs ?? 0);
  }

  get activatedBackstockTickets(): number {
    return Number(this._data?.activated_backstock?.tickets ?? 0);
  }

  private _getLocationBucket(bucket: 'cashier' | 'locker' | 'vending' | 'missing' | 'missing_reconciled' | 'backstock_total') {
    const fallbackBackstock = {
      packs: this.activatedBackstockPacks,
      tickets: this.activatedBackstockTickets,
    };
    const bucketData = this._data?.location_buckets?.[bucket];
    if (!bucketData) {
      return bucket === 'backstock_total' ? fallbackBackstock : { packs: 0, tickets: 0 };
    }
    return {
      packs: Number(bucketData.packs ?? 0),
      tickets: Number(bucketData.tickets ?? 0),
    };
  }

  get backstockCashierPacks(): number {
    return this._getLocationBucket('cashier').packs;
  }

  get backstockCashierTickets(): number {
    return this._getLocationBucket('cashier').tickets;
  }

  get backstockLockerPacks(): number {
    return this._getLocationBucket('locker').packs;
  }

  get backstockLockerTickets(): number {
    return this._getLocationBucket('locker').tickets;
  }

  get backstockVendingPacks(): number {
    return this._getLocationBucket('vending').packs;
  }

  get backstockVendingTickets(): number {
    return this._getLocationBucket('vending').tickets;
  }

  get backstockMissingPacks(): number {
    return this._getLocationBucket('missing').packs;
  }

  get backstockMissingTickets(): number {
    return this._getLocationBucket('missing').tickets;
  }

  get backstockMissingReconciledPacks(): number {
    return this._getLocationBucket('missing_reconciled').packs;
  }

  get backstockMissingReconciledTickets(): number {
    return this._getLocationBucket('missing_reconciled').tickets;
  }

  get backstockTotalPacks(): number {
    return this._getLocationBucket('backstock_total').packs;
  }

  get backstockTotalTickets(): number {
    return this._getLocationBucket('backstock_total').tickets;
  }

  get backstockValue(): number {
    return Number(this._data?.backstock_value ?? 0);
  }

  get weeklySold(): number {
    return Number(this._data?.weekly_sold ?? 0);
  }

  get weeklySales(): number {
    return Number(this._data?.weekly_sales ?? 0);
  }

  get notActivated(): number {
    return Number(this._data?.not_activated?.packs ?? this._data?.not_activated ?? 0);
  }


  toRowFormat() {
    return {
      game_id: this.gameId,
      date_range: this.dateRangeLabel,
      game_number: this.gameId,
      upc: this.upc,
      game_name: this.name,
      start_date: this.startDate,
      end_date: this.endDate,
      rate: this.rate,
      tickets_per_pack: this.ticketsPerPack,
      activated_packs_count: this.activatedCountPacks,
      activated_count_tickets: this.activatedCountTickets,
      on_counter_count: this.activatedOnCounterPacks,
      activated_on_counter_tickets: this.activatedOnCounterTickets,
      on_counter_value: this.onCounterValue,
      activated_backstock: this.backstockTotalPacks,
      activated_backstock_tickets: this.backstockTotalTickets,
      backstock_cashier: this.backstockCashierPacks,
      backstock_cashier_tickets: this.backstockCashierTickets,
      backstock_locker: this.backstockLockerPacks,
      backstock_locker_tickets: this.backstockLockerTickets,
      backstock_vending: this.backstockVendingPacks,
      backstock_vending_tickets: this.backstockVendingTickets,
      backstock_missing: this.backstockMissingPacks,
      backstock_missing_tickets: this.backstockMissingTickets,
      backstock_missing_reconciled: this.backstockMissingReconciledPacks,
      backstock_missing_reconciled_tickets: this.backstockMissingReconciledTickets,
      backstock_total: this.backstockTotalPacks,
      backstock_total_tickets: this.backstockTotalTickets,
      backstock_value: this.backstockValue,
      weekly_sold: this.weeklySold,
      weekly_sales: this.weeklySales,
      not_activated: this.notActivated,
    };
  }

  private _formatLabelDate(value: string): string {
    if (!value || value.toLowerCase() === 'na') {
      return 'Present';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  get dateRangeLabel(): string {
    const startLabel = this._formatLabelDate(this.startDate);
    return startLabel;
  }

  static fromServerData(gameData: any) {
    return new GamePackGameModel(gameData);
  }
}

class GamePackReportModel {
  private _summary: GamePackSummaryModel;
  private _games: GamePackGameModel[];
  private _rawData: any;

  constructor(serverData: any = null) {
    this._rawData = serverData;
    this._summary = GamePackSummaryModel.fromServerData(serverData);
    const games = Array.isArray(serverData?.games) ? serverData.games : [];
    this._games = games.map((game: any) => GamePackGameModel.fromServerData(game));
  }

  get summary(): GamePackSummaryModel {
    return this._summary;
  }

  toPackExportRows(visibleGameIds?: string[]) {
    const visibleGameIdSet = visibleGameIds ? new Set(visibleGameIds) : null;
    const games = Array.isArray(this._rawData?.games) ? this._rawData.games : [];

    return games.flatMap((game: any) => {
      const gameId = String(game?.game_id ?? '');
      if (visibleGameIdSet && !visibleGameIdSet.has(gameId)) {
        return [];
      }

      const packs = Array.isArray(game?.packs) ? game.packs : [];

      return packs.map((pack: any) => {
        const locationLastEvent = pack?.location_last_event ?? {};

        return {
          game_id: gameId,
          game_name: game?.name ?? '',
          upc: game?.upc ?? '',
          game_start_date: game?.start_date ?? '',
          game_end_date: game?.end_date ?? '',
          game_rate: Number(game?.rate ?? 0),
          game_tickets_per_pack: Number(game?.tickets_per_pack ?? 0),
          pack_id: pack?.pack_id ?? '',
          pack_status: pack?.status ?? '',
          tickets_sold: Number(pack?.tickets_sold ?? 0),
          returned_tickets: Number(pack?.returned_tickets ?? 0),
          remaining_tickets: Number(pack?.remaining_tickets ?? 0),
          ending_ticket_number: pack?.ending_ticket_number ?? '',
          activated_date: pack?.activated_date ?? '',
          settled_date: pack?.settled_date ?? '',
          is_activated: Boolean(pack?.is_activated),
          is_backstock: Boolean(pack?.is_backstock),
          is_pseudo_activated: Boolean(pack?.is_pseudo_activated),
          is_placeholder: Boolean(pack?.is_placeholder),
          is_in_audit: Boolean(pack?.is_in_audit),
          audit_detail_id: pack?.audit_detail_id ?? '',
          pack_location: pack?.pack_location ?? '',
          backstock_state: pack?.backstock_state ?? '',
          last_backstock_location: pack?.last_backstock_location ?? '',
          location_entered_at: pack?.location_entered_at ?? '',
          location_updated_by_user_name: pack?.location_updated_by_user_name ?? '',
          location_confidence: pack?.location_confidence ?? '',
          location_age_days: pack?.location_age_days ?? '',
          location_last_event_at: locationLastEvent?.event_at ?? '',
          location_last_event_source: locationLastEvent?.source ?? '',
          location_last_event_from_location: locationLastEvent?.from_location ?? '',
          location_last_event_to_location: locationLastEvent?.to_location ?? '',
          location_last_event_changed_by_user_name: locationLastEvent?.changed_by_user_name ?? '',
        };
      });
    });
  }

  toRowFormat() {
    return this._games.map(game => game.toRowFormat());
  }

  static fromServerData(serverData: any) {
    return new GamePackReportModel(serverData);
  }

  static createEmpty() {
    return new GamePackReportModel({});
  }
}

export default GamePackReportModel;
export { GamePackSummaryModel, GamePackGameModel };
